/**
 * Exactly what the unsaved edits change, cell by cell, so a planner can read them before Save
 * (1st Phorm, 10/29: the save dialog showed only the plan total's change, after the fact).
 *
 * Both trees are read the way the version comparison reads them (`walk_version_leaf_periods`:
 * per leaf, per period, actual rows over plan rows), so a parent's roll-up never shows up as a
 * change of its own — only the leaves that moved, plus rows added or removed.
 */
import { walk_version_leaf_periods, type VersionLeafValue, type VersionPathSegment } from "./version_compare"

export type UnsavedField = "store_count" | "cases_sold" | "units_sold" | "volume_dollars"

export interface UnsavedCellChange {
  /** Row names from the top of the tree to the leaf, e.g. `Walmart @ Direct › Protein Sticks › Original`. */
  path: string
  period: string
  field: UnsavedField
  before: number
  after: number
}

export interface UnsavedRowChange {
  path: string
  kind: "added" | "removed"
}

export interface UnsavedDiff {
  rows: UnsavedRowChange[]
  /** Cell changes, in tree then period order, capped at the `limit` asked for. */
  cells: UnsavedCellChange[]
  /** Every changed cell, including those past the cap. */
  cell_count: number
}

const FIELDS: UnsavedField[] = ["store_count", "cases_sold", "units_sold", "volume_dollars"]

/** Below this a difference is float dust from re-aggregation, not an edit. */
const EPSILON = 0.005

function leaf_map(nodes: unknown[]): { order: string[]; values: Map<string, Map<string, VersionLeafValue>> } {
  const order: string[] = []
  const values = new Map<string, Map<string, VersionLeafValue>>()
  walk_version_leaf_periods(nodes, 0, (segments: VersionPathSegment[], period: string, value: VersionLeafValue) => {
    const path = segments.map((segment: VersionPathSegment) => segment.name).join(" › ")
    let periods = values.get(path)
    if (!periods) {
      periods = new Map()
      values.set(path, periods)
    }
    periods.set(period, value)
  })
  // Tree order, and a leaf with no sales rows still exists.
  function visit(node: any, trail: string[]): void {
    const next = [...trail, String(node?.name ?? "").trim()]
    const children = Array.isArray(node?.children) ? node.children : []
    if (children.length > 0) {
      for (const child of children) visit(child, next)
      return
    }
    const path = next.join(" › ")
    if (!values.has(path)) values.set(path, new Map())
    if (!seen.has(path)) {
      seen.add(path)
      order.push(path)
    }
  }
  const seen = new Set<string>()
  for (const node of nodes) visit(node, [])
  return { order, values }
}

function field_value(value: VersionLeafValue | undefined, field: UnsavedField): number {
  if (!value) return 0
  const raw = value[field]
  return raw == null ? 0 : Number(raw)
}

export function diff_unsaved_forecast(saved: unknown[], current: unknown[], limit: number = 200): UnsavedDiff {
  const before = leaf_map(saved)
  const after = leaf_map(current)
  const rows: UnsavedRowChange[] = []
  const cells: UnsavedCellChange[] = []
  let cell_count = 0

  for (const path of after.order) {
    if (!before.values.has(path)) rows.push({ path, kind: "added" })
  }
  for (const path of before.order) {
    if (!after.values.has(path)) rows.push({ path, kind: "removed" })
  }

  for (const path of after.order) {
    const was = before.values.get(path)
    if (!was) continue
    const now = after.values.get(path)!
    const periods = [...new Set([...was.keys(), ...now.keys()])].sort()
    for (const period of periods) {
      for (const field of FIELDS) {
        const before_value = field_value(was.get(period), field)
        const after_value = field_value(now.get(period), field)
        if (Math.abs(after_value - before_value) < EPSILON) continue
        // Units follow cases on a row with a pack size; listing both says the same edit twice.
        if (field === "units_sold" && cells.some((c: UnsavedCellChange) => c.path === path && c.period === period && c.field === "cases_sold")) continue
        cell_count += 1
        if (cells.length < limit) cells.push({ path, period, field, before: before_value, after: after_value })
      }
    }
  }
  return { rows, cells, cell_count }
}

/** Parse the saved-state signature (`JSON.stringify` of the tree) back into a tree, or `[]`. */
export function saved_tree_from_signature(signature: string): unknown[] {
  if (!signature) return []
  try {
    const parsed = JSON.parse(signature)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function leaf(name: string, rows: Array<{ period: string; cases: number; stores: number; dollars: number }>): any {
    return {
      name,
      level: "sku",
      sales_data: rows.map((r) => ({
        period: { period_key: r.period },
        source_kind: "forecast",
        metrics: { cases_sold: r.cases, store_count: r.stores, volume_dollars: r.dollars },
      })),
    }
  }
  function customer(name: string, children: any[]): any {
    return { name, level: "customer", children }
  }

  describe("diff_unsaved_forecast", () => {
    it("lists exactly the leaf cells that moved, not the parents that rolled up", () => {
      const saved = [customer("Walmart @ Direct", [leaf("Protein Sticks", [{ period: "2027-01", cases: 48, stores: 10, dollars: 4800 }])])]
      const current = [customer("Walmart @ Direct", [leaf("Protein Sticks", [{ period: "2027-01", cases: 48.7805, stores: 10, dollars: 4878.05 }])])]
      const diff = diff_unsaved_forecast(saved, current)
      expect(diff.rows).toEqual([])
      expect(diff.cells).toEqual([
        { path: "Walmart @ Direct › Protein Sticks", period: "2027-01", field: "cases_sold", before: 48, after: 48.7805 },
        { path: "Walmart @ Direct › Protein Sticks", period: "2027-01", field: "volume_dollars", before: 4800, after: 4878.05 },
      ])
      expect(diff.cell_count).toBe(2)
    })

    it("names added and removed rows without listing their cells", () => {
      const saved = [customer("Target @ Direct", [leaf("Level-1 Bar", [{ period: "2027-01", cases: 5, stores: 2, dollars: 500 }])])]
      const current = [
        customer("Target @ Direct", []),
        customer("Kroger @ KeHE", [leaf("Protein Sticks", [{ period: "2027-01", cases: 3, stores: 1, dollars: 300 }])]),
      ]
      const diff = diff_unsaved_forecast(saved, current)
      expect(diff.rows).toEqual([
        { path: "Target @ Direct", kind: "added" },
        { path: "Kroger @ KeHE › Protein Sticks", kind: "added" },
        { path: "Target @ Direct › Level-1 Bar", kind: "removed" },
      ])
      expect(diff.cells).toEqual([])
    })

    it("caps the list but counts every change", () => {
      const months = ["2027-01", "2027-02", "2027-03"]
      const saved = [leaf("A", months.map((period: string) => ({ period, cases: 1, stores: 1, dollars: 0 })))]
      const current = [leaf("A", months.map((period: string) => ({ period, cases: 2, stores: 1, dollars: 0 })))]
      const diff = diff_unsaved_forecast(saved, current, 2)
      expect(diff.cells.length).toBe(2)
      expect(diff.cell_count).toBe(3)
    })

    it("reads the saved signature back, and an unreadable one as an empty tree", () => {
      expect(saved_tree_from_signature('[{"name":"A"}]')).toEqual([{ name: "A" }])
      expect(saved_tree_from_signature("")).toEqual([])
      expect(saved_tree_from_signature("{oops")).toEqual([])
    })
  })
}
